import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import SearchHeader from '../components/SearchHeader';
import TopNav from '../components/TopNav';
import { darkPurple, accent } from '../utils/colors';
import { fetchSkillById } from '../utils/api';

export default function SkillDetailsPage({ apiBase }) {
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchDropdown, setSearchDropdown] = useState(false);
  const searchInputRef = React.useRef();

  useEffect(() => {
    const hash = (window.location.hash || '').replace('#/skill/', '');
    const id = parseInt(hash, 10);
    if (!apiBase || !id) { 
      setLoading(false); 
      return; 
    }
    
    const loadSkill = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchSkillById(apiBase, id);
        setSkill(data);
      } catch (e) {
        setError(e.message || 'Ошибка загрузки навыка');
      } finally {
        setLoading(false);
      }
    };

    loadSkill();
  }, [apiBase]);

  const goBackToMatrix = () => {
    window.location.hash = '#/matrix';
  };

  return (
    <div className="App" style={{ background: darkPurple, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SearchHeader
        search={search}
        setSearch={setSearch}
        searchInputRef={searchInputRef}
        setSearchDropdown={setSearchDropdown}
        searchDropdown={searchDropdown}
      />
      <TopNav current="matrix" />
      <div style={{ padding: 12 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 4px 24px #a259f733' }}>
          {/* Кнопка возврата к матрице */}
          <div style={{ marginBottom: 16 }}>
            <button 
              onClick={goBackToMatrix}
              style={{ 
                background: accent, 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6, 
                padding: '8px 16px', 
                fontSize: 14, 
                fontWeight: 600, 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              ← Вернуться к матрице навыков
            </button>
          </div>

          {loading ? (
            <div style={{ color: darkPurple, textAlign: 'center', padding: '40px 0' }}>
              Загрузка навыка...
            </div>
          ) : error ? (
            <div style={{ color: 'red', textAlign: 'center', padding: '40px 0' }}>
              {error}
            </div>
          ) : !skill ? (
            <div style={{ color: '#888', textAlign: 'center', padding: '40px 0' }}>
              Навык не найден
            </div>
          ) : (
            <>
              {/* Заголовок навыка */}
              <div style={{ marginBottom: 24 }}>
                <h1 style={{ 
                  color: accent, 
                  margin: '0 0 8px 0', 
                  fontSize: 28, 
                  fontWeight: 700,
                  lineHeight: 1.2
                }}>
                  {skill.title}
                </h1>
                {skill.parentId && (
                  <div style={{ 
                    color: '#666', 
                    fontSize: 14, 
                    marginBottom: 8 
                  }}>
                    ID: {skill.id} | Parent ID: {skill.parentId}
                  </div>
                )}
              </div>

              {/* Описание навыка */}
              {skill.description ? (
                <div className="markdown-block" style={{ lineHeight: 1.6 }}>
                  <ReactMarkdown
                    components={{
                      h1: ({children}) => <h1 style={{color: accent, fontSize: '1.8em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h1>,
                      h2: ({children}) => <h2 style={{color: accent, fontSize: '1.5em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h2>,
                      h3: ({children}) => <h3 style={{color: accent, fontSize: '1.3em', marginTop: '1.5em', marginBottom: '0.5em', fontWeight: 600}}>{children}</h3>,
                      p: ({children}) => <p style={{marginBottom: '1em'}}>{children}</p>,
                      ul: ({children}) => <ul style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ul>,
                      ol: ({children}) => <ol style={{marginBottom: '1em', paddingLeft: '2em'}}>{children}</ol>,
                      li: ({children}) => <li style={{marginBottom: '0.5em'}}>{children}</li>,
                      code: ({children}) => <code style={{background: '#f3eaff', padding: '2px 6px', borderRadius: 4, fontFamily: 'monospace', fontSize: '0.9em'}}>{children}</code>,
                      pre: ({children}) => <pre style={{background: '#f3eaff', padding: '1em', borderRadius: 8, overflow: 'auto', marginBottom: '1em'}}>{children}</pre>,
                      blockquote: ({children}) => <blockquote style={{borderLeft: `4px solid ${accent}`, paddingLeft: '1em', marginLeft: 0, color: '#6a1b9a', fontStyle: 'italic'}}>{children}</blockquote>,
                      strong: ({children}) => <strong style={{fontWeight: 600, color: accent}}>{children}</strong>,
                      em: ({children}) => <em style={{fontStyle: 'italic'}}>{children}</em>,
                      a: ({children, href}) => <a href={href} style={{color: accent, textDecoration: 'none'}} target="_blank" rel="noopener noreferrer">{children}</a>
                    }}
                  >
                    {skill.description.replace(/\r\n|\r/g, '\n').replace(/^[ \t]+/gm, '')}
                  </ReactMarkdown>
                </div>
              ) : (
                <div style={{ 
                  color: "#888", 
                  fontSize: 18, 
                  textAlign: 'center',
                  padding: '40px 0',
                  fontStyle: 'italic'
                }}>
                  Описание навыка отсутствует
                </div>
              )}

              {/* Дополнительная информация */}
              <div style={{ 
                marginTop: 32, 
                padding: 16, 
                background: '#f8f9fa', 
                borderRadius: 8,
                border: `1px solid ${accent}20`
              }}>
                <h3 style={{ color: accent, margin: '0 0 12px 0', fontSize: 16, fontWeight: 600 }}>
                  Информация о навыке
                </h3>
                <div style={{ fontSize: 14, color: '#666' }}>
                  <div style={{ marginBottom: 4 }}><strong>ID:</strong> {skill.id}</div>
                  <div style={{ marginBottom: 4 }}><strong>DepartmentId:</strong> {skill.departmentId}</div>
                  {skill.parentId && <div style={{ marginBottom: 4 }}><strong>Родительский ID:</strong> {skill.parentId}</div>}
                  <div style={{ marginBottom: 4 }}><strong>Уровень:</strong> {skill.level}</div>
                  <div style={{ marginBottom: 4 }}><strong>Порядковый номер:</strong> {skill.orderNum}</div>
                  <div><strong>Имеет дочерние элементы:</strong> {skill.hasChildren ? 'Да' : 'Нет'}</div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

